import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { getLiveFeed, type LiveFeedOrder } from '../utils/liveFeed';
type LiveOrderTickerProps = {
  isDispatchActive?: boolean;
  opensIn?: string;
  deliveryMessage?: string;
};
export function LiveOrderTicker({
  isDispatchActive = true,
  opensIn = '',
  deliveryMessage = ''
}: LiveOrderTickerProps) {
  const shouldReduceMotion = useReducedMotion();
  const [now, setNow] = useState(() => new Date());
  const [closedMessageIndex, setClosedMessageIndex] = useState(0);
  const [activeOrderIndex, setActiveOrderIndex] = useState(0);
  // Weighted, persisted feed (see utils/liveFeed). Timestamps are absolute and
  // stored, so "40 min ago" becomes "2 hr ago" two hours later instead of
  // resetting every page load.
  const [orders, setOrders] = useState<LiveFeedOrder[]>(() => getLiveFeed());
  useEffect(() => {
    // When dispatch closes, lock the relative time in place so this reads as
    // the last completed feed rather than implying orders are still arriving.
    setNow(new Date());
    if (!isDispatchActive) return;
    const timer = window.setInterval(() => {
      setNow(new Date());
      // Re-reading the feed each minute ages entries off the board and lets
      // new orders arrive during a long session.
      setOrders(getLiveFeed());
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [isDispatchActive]);
  useEffect(() => {
    if (isDispatchActive || shouldReduceMotion) {
      setClosedMessageIndex(0);
      return;
    }
    const timer = window.setInterval(() => {
      setClosedMessageIndex(index => (index + 1) % 2);
    }, 5_000);
    return () => window.clearInterval(timer);
  }, [isDispatchActive, shouldReduceMotion]);
  useEffect(() => {
    if (!isDispatchActive || shouldReduceMotion) {
      setActiveOrderIndex(0);
      return;
    }
    const timer = window.setInterval(() => {
      setActiveOrderIndex(index => (index + 1) % orders.length);
    }, 7_000);
    return () => window.clearInterval(timer);
  }, [isDispatchActive, orders.length, shouldReduceMotion]);
  if (!isDispatchActive) {
    const availabilityMessage = 'Dispatch closed · Order online 24/7';
    const nextDeliveryMessage = deliveryMessage.includes('Monday') ? 'Monday delivery available' : 'Next day delivery available';
    const closedMessages = [availabilityMessage, nextDeliveryMessage];
    const activeMessage = closedMessages[closedMessageIndex];
    return <div className="flex min-w-0 flex-1 items-center overflow-hidden" aria-label={`${availabilityMessage}. ${nextDeliveryMessage}.`} data-id="element-1125" data-mp="bf56b2kdqmmb">
        <span className="mr-2 flex h-3 shrink-0 items-end gap-0.5" aria-hidden="true" data-id="element-1126" data-mp="6j6et63r8oho">
          <span className="h-1.5 w-0.5 rounded-full bg-white/30" data-id="element-1127" data-mp="1jtqegx8j9cz" />
          <span className="h-3 w-0.5 rounded-full bg-white/30" data-id="element-1128" data-mp="20u1x504qrhxn" />
          <span className="h-2 w-0.5 rounded-full bg-white/30" data-id="element-1129" data-mp="1vw0ciaro3ak2" />
        </span>
        <div className="min-w-0 overflow-hidden" data-id="element-1130" data-mp="1vtxilfne8xa2">
          <AnimatePresence initial={false} mode="wait" data-id="element-1131" data-mp="24bzekh6wijbc">
            <motion.p key={activeMessage} initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={shouldReduceMotion ? undefined : {
            opacity: 0,
            y: -12
          }} transition={{
            duration: 0.5,
            ease: [0.22, 1, 0.36, 1]
          }} className="truncate whitespace-nowrap font-medium tracking-wide text-white/70 text-[12px]" data-id="element-1132" data-mp="24b7r5tngjrw5">
              {activeMessage}
            </motion.p>
          </AnimatePresence>
        </div>
      </div>;
  }

  // Index is guarded because the feed length can change when entries age off.
  const activeOrder = orders[activeOrderIndex % orders.length];
  if (!activeOrder) return null;
  const activeOrderMessage = `${activeOrder.quantity} yd ${activeOrder.material} → ${activeOrder.city} · ${formatRelativeTime(activeOrder.createdAt, now)}`;
  return <div className="flex min-w-0 flex-1 items-center overflow-hidden" aria-label={`Recent order: ${activeOrderMessage}`} data-id="element-1133" data-mp="3at4hjdp4ggmf">
      <div className="mr-2 flex shrink-0 items-center gap-1.5" data-id="element-1134" data-mp="njvlaalybft2">
        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#00b774]" data-id="element-1135" data-mp="nmi0mwf32zlu">Live</span>
        <span className="flex h-3 items-end gap-0.5" aria-hidden="true" data-id="element-1136" data-mp="wlbvoicrnhxa">
          {[0, 1, 2].map(index => <motion.span key={index} className="w-0.5 rounded-full bg-[#00b774]" animate={{
          height: ['35%', '100%', '35%']
        }} transition={{
          duration: 1.15,
          repeat: Infinity,
          delay: index * 0.18,
          ease: 'easeInOut'
        }} data-id="element-1137" data-mp="rm8pgul1v5nq" />)}
        </span>
      </div>
      <span className="mr-2 shrink-0 text-white/30" aria-hidden="true" data-id="element-1138" data-mp="1fbl8em66is38">•</span>
      <div className="min-w-0 overflow-hidden" data-id="element-1139" data-mp="1fdnw2zjv094s">
        <AnimatePresence initial={false} mode="wait" data-id="element-1140" data-mp="1bbar4dcxsf92">
          <motion.p key={`${activeOrder.createdAt}-${activeOrder.material}`} initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={shouldReduceMotion ? undefined : {
          opacity: 0,
          y: -12
        }} transition={{
          duration: 0.5,
          ease: [0.22, 1, 0.36, 1]
        }} className="truncate whitespace-nowrap text-left text-[11px] font-medium text-white/50 sm:text-xs" data-id="element-1141" data-mp="1bclwuipxzx2t">
            <span className="text-white/70" data-id="element-1142" data-mp="1u0wfuor8hmsd">{activeOrder.quantity} yd {activeOrder.material}</span>
            <span className="mx-1.5 text-[#00b774]" data-id="element-1143" data-mp="1yzfx4buvuaee">→</span>
            <span data-id="element-1144" data-mp="1l5ge7lk8m3c8">{activeOrder.city}</span>
            <span className="mx-1.5 text-white/35" data-id="element-1145" data-mp="1pwybnzn1xlsg">·</span>
            <span className="text-white/35" data-id="element-1146" data-mp="1pyhu9ww6k8xe">{formatRelativeTime(activeOrder.createdAt, now)}</span>
          </motion.p>
        </AnimatePresence>
      </div>
    </div>;
}
const PT = 'America/Los_Angeles';

/** Calendar day key in Pacific time, so "yesterday" means yesterday here. */
function pacificDayKey(ts: number): string {
  return new Date(ts).toLocaleDateString('en-US', {
    timeZone: PT
  });
}
function pacificClock(ts: number): string {
  return new Date(ts).toLocaleTimeString('en-US', {
    timeZone: PT,
    hour: 'numeric',
    minute: '2-digit'
  });
}

/**
 * Same Pacific day → counts up in minutes/hours. Earlier days → named, since
 * "19 hr ago" is harder to read than "Yesterday 2:40 PM".
 */
function formatRelativeTime(createdAt: number, now: Date): string {
  const nowMs = now.getTime();
  const today = pacificDayKey(nowMs);
  const created = pacificDayKey(createdAt);
  if (created === today) {
    const minutes = Math.max(1, Math.round((nowMs - createdAt) / 60_000));
    if (minutes < 60) return `${minutes} min ago`;
    return `${Math.floor(minutes / 60)} hr ago`;
  }
  if (created === pacificDayKey(nowMs - 24 * 60 * 60 * 1000)) {
    return `Yesterday ${pacificClock(createdAt)}`;
  }
  const weekday = new Date(createdAt).toLocaleDateString('en-US', {
    timeZone: PT,
    weekday: 'short'
  });
  return `${weekday} ${pacificClock(createdAt)}`;
}